import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { deleteApplication } from "../redux/applicationsSlice";
import { User, Mail, Phone, Briefcase, Code, FileText, Calendar, Trash2, ArrowLeft } from "lucide-react";

const ApplicationSummary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const application = useSelector((state) =>
    state.applications.find((app) => app.id === parseInt(id))
  );

  if (!application) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-red-50 p-4 rounded-full mb-4">
          <FileText className="text-red-500 w-12 h-12" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Application Not Found</h2>
        <p className="text-slate-500 mb-6">The application you're looking for might have been deleted.</p>
        <button onClick={() => navigate("/applications")} className="btn-primary">
          Back to Applications
        </button>
      </div>
    );
  }

  const detailItems = [
    { icon: <User size={18} />, label: "Full Name", value: application.name },
    { icon: <Mail size={18} />, label: "Email Address", value: application.email },
    { icon: <Phone size={18} />, label: "Phone Number", value: application.phone },
    { icon: <Briefcase size={18} />, label: "Experience", value: application.experience },
    { icon: <Calendar size={18} />, label: "Available From", value: application.startDate },
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <button
        onClick={() => navigate("/applications")}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-8 group"
      >
        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
        Back to Applications
      </button>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Application Details</h1>
          <p className="text-slate-500">Submitted for Job ID: {application.jobId}</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              if (window.confirm("Are you sure you want to delete this application?")) {
                dispatch(deleteApplication(application.id));
                navigate("/applications");
              }
            }}
            className="flex items-center gap-2 px-6 py-3 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors border border-red-100"
          >
            <Trash2 size={18} />
            Delete
          </button>
          <button
            onClick={() => navigate(`/apply/${application.id}`)}
            className="btn-primary !py-3 !px-8"
          >
            Edit Info
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          {/* Main Info */}
          <div className="glass-card rounded-3xl p-8 border border-white/40 shadow-xl shadow-slate-900/5">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <User className="text-blue-600" size={20} />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {detailItems.map((item, idx) => (
                <div key={idx}>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                    {item.label}
                  </p>
                  <p className="text-slate-700 font-semibold flex items-center gap-2">
                    <span className="text-slate-300">{item.icon}</span>
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Letter */}
          <div className="glass-card rounded-3xl p-8 border border-white/40 shadow-xl shadow-slate-900/5">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <FileText className="text-blue-600" size={20} />
              Cover Letter
            </h3>
            <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 italic text-slate-600 leading-relaxed">
              "{application.coverLetter}"
            </div>
          </div>
        </div>

        <div className="md:col-span-1">
          <div className="glass-card rounded-3xl p-8 border border-white/40 shadow-xl shadow-slate-900/5 sticky top-32">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Code className="text-blue-600" size={20} />
              Technical Skills
            </h3>
            <div className="flex flex-wrap gap-2">
              {String(application.skills).split(",").map((skill, i) => (
                <span
                  key={i}
                  className="bg-blue-50 text-blue-600 px-3 py-1.5 rounded-lg text-sm font-bold border border-blue-100"
                >
                  {skill.trim()}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationSummary;

